import {QueryResult} from '@apollo/react-common';
import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import {PopupDropdown} from '@wandb/common/components/PopupDropdown';
import {isNotNullOrUndefined} from '@wandb/common/util/types';
import {ApolloCurrentQueryResult} from 'apollo-client';
import * as _ from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {ExecutionResult} from 'react-apollo';
import {
  Button,
  Checkbox,
  Input,
  InputOnChangeData,
  Modal,
  Popup,
} from 'semantic-ui-react';
import {Viewer} from 'src/state/viewer/types';

import {
  CreateRunQueueMutation,
  FetchLaunchAgentsFromProjectQuery,
  RunQueue,
  RunQueueAccessType,
  RunQueueItem,
  useCreateRunQueueMutation,
  useDeleteRunQueuesMutation,
  useRunsStateQuery,
  useTeamInfoQuery,
} from '../generated/graphql';
import {ProjectPageQueryData} from '../state/graphql/projectPageQuery';
import {runNamesFilter, toMongo} from '../util/filters';
import ActiveRunIndicator from './ActiveRunIndicator';
import EditLaunchConfigModal, {
  CreateRunQueueArgs,
  createRunQueueByName,
} from './EditLaunchConfigModal';
import {RunQueueItemsTable} from './RunQueueItemsTable';
import * as S from './RunQueueTab.styles';
import {InstrumentedLoader as Loader} from './utility/InstrumentedLoader';

export const DEFAULT_RUN_QUEUE_NAME = 'default';

interface RunQueueTabProps {
  viewer?: Viewer;
  entityName: string;
  projectLoading: boolean;
  projectName: string;
  isTeam: boolean;
  runQueues: RunQueue[];
  launchAgentsQuery: QueryResult<FetchLaunchAgentsFromProjectQuery>;
  projectWriteAccess: boolean;
  refetchProject: () => Promise<ApolloCurrentQueryResult<ProjectPageQueryData>>;
}

const RunQueueTab: React.FC<RunQueueTabProps> = props => {
  const [creatingRunQueue, setCreatingRunQueue] = useState(false);
  const [deleteRunQueue, setDeleteRunQueue] = useState<RunQueue | null>();
  const [createRunQueue] = useCreateRunQueueMutation();
  const sortedRunQueues = _.sortBy(props.runQueues, rq =>
    new Date(rq.createdAt + 'Z').getTime()
  );
  const memberData = useTeamInfoQuery({
    variables: {entityName: props.entityName},
  });
  const viewer = props.viewer;
  const teamMembers = memberData?.data?.entity?.members;

  const launchAgents = props.launchAgentsQuery.data?.project?.launchAgents;
  const launchAgentsMap = useMemo(() => {
    if (launchAgents != null) {
      return Object.fromEntries(launchAgents.map(la => [la.id, la.name]));
    }
    return {};
  }, [launchAgents]);
  useEffect(() => {
    window.analytics?.track('Viewed launch run queues tab', {
      entity: viewer?.entity,
      sourceEntity: props.entityName,
      sourceProject: props.projectName,
    });
  }, [props.entityName, props.projectName, viewer]);

  function isTeamMember() {
    if (viewer == null) {
      return false;
    } else if (teamMembers != null) {
      return (
        teamMembers.findIndex(member => member.username === viewer.username) !==
        -1
      );
    }
    return false;
  }

  function isTeamAdmin() {
    if (viewer == null) {
      return false;
    } else if (teamMembers != null) {
      const member = teamMembers.find(m => m.username === viewer.username);
      if (member == null) {
        return false;
      }
      return member.admin ?? false;
    }
    return false;
  }

  const viewerIsTeamMember = props.isTeam && isTeamMember();
  const viewerIsTeamAdmin = props.isTeam && isTeamAdmin();
  const isViewersProject = props.entityName === viewer?.entity;
  const canCreateRunQueue = isViewersProject || viewerIsTeamMember;
  const canDeleteProjectAccessQueue = isViewersProject || viewerIsTeamAdmin;
  const showTabContents = viewer != null && memberData.data != null;
  if (!showTabContents) {
    return <Loader name="run-queue-tab-contents-loader" />;
  }
  return (
    <>
      <RunQueueTabContents
        {...props}
        runQueues={sortedRunQueues}
        canCreateRunQueue={canCreateRunQueue}
        creatingRunQueue={creatingRunQueue}
        launchAgentsMap={launchAgentsMap}
        canDeleteProjectAccessQueue={canDeleteProjectAccessQueue}
        createRunQueue={createRunQueue}
        setCreatingRunQueue={setCreatingRunQueue}
        setDeleteRunQueue={setDeleteRunQueue}
        projectWriteAccess={props.projectWriteAccess}
      />
      {creatingRunQueue && (
        <AddRunQueueModal
          open
          onClose={() => {
            setCreatingRunQueue(false);
          }}
          entityName={props.entityName}
          projectName={props.projectName}
          isTeam={props.isTeam}
          runQueues={props.runQueues}
          refetchProject={props.refetchProject}
        />
      )}
      {deleteRunQueue && (
        <DeleteRunQueueModal
          open
          runQueue={deleteRunQueue}
          projectName={props.projectName}
          entityName={props.entityName}
          onClose={() => {
            setDeleteRunQueue(null);
          }}
          onDelete={async () => {
            await props.refetchProject();
            setDeleteRunQueue(null);
          }}
        />
      )}
    </>
  );
};

export default RunQueueTab;

interface RunQueueTabContentsProps extends RunQueueTabProps {
  runQueues: RunQueue[];
  canCreateRunQueue: boolean;
  creatingRunQueue: boolean;
  launchAgentsMap: {[key: number]: string};
  canDeleteProjectAccessQueue: boolean;
  isTeam: boolean;
  projectWriteAccess: boolean;
  createRunQueue: (
    variables: CreateRunQueueArgs
  ) => Promise<ExecutionResult<CreateRunQueueMutation>>;
  setDeleteRunQueue(state: RunQueue): void;
  setCreatingRunQueue(state: boolean): void;
}

const RunQueueTabContents: React.FC<RunQueueTabContentsProps> = ({
  runQueues,
  projectName,
  entityName,
  canCreateRunQueue,
  projectLoading,
  launchAgentsMap,
  canDeleteProjectAccessQueue,
  isTeam,
  projectWriteAccess,
  createRunQueue,
  setCreatingRunQueue,
  setDeleteRunQueue,
  refetchProject,
}) => {
  const [autoCreatingRunQueue, setAutoCreatingRunQueue] = useState(false);
  const [runQueuesFilter, setRunQueuesFilter] = useState('');

  useEffect(() => {
    if (!projectLoading && runQueues.length === 0 && !autoCreatingRunQueue) {
      setAutoCreatingRunQueue(true);
      const autoCreateDefaultRunQueue = async () => {
        await createRunQueueByName(
          createRunQueue,
          'default',
          entityName,
          projectName
        );
        await refetchProject();
        setAutoCreatingRunQueue(false);
      };
      autoCreateDefaultRunQueue();
    }
  }, [
    runQueues,
    autoCreatingRunQueue,
    entityName,
    projectName,
    projectLoading,
    refetchProject,
    setAutoCreatingRunQueue,
    createRunQueue,
  ]);

  if (runQueues.length === 0) {
    return <Loader name="no-run-queues-loader" />;
  }
  const filteredRunQueues = runQueues.filter(rq =>
    rq.name.includes(runQueuesFilter)
  );
  return (
    <>
      <S.RunQueueTabHeader>
        <div>
          <Input
            icon={{
              className: 'wbic-ic-search',
            }}
            iconPosition="left"
            placeholder="Search queues"
            value={runQueuesFilter}
            onChange={(e, data) => {
              setRunQueuesFilter(data.value);
            }}
          />
        </div>
        {canCreateRunQueue && (
          <S.CreateQueueDiv
            onClick={() => {
              setCreatingRunQueue(true);
            }}>
            + Create Queue
          </S.CreateQueueDiv>
        )}
      </S.RunQueueTabHeader>
      {filteredRunQueues.length > 0 &&
        filteredRunQueues.map((r, index) => {
          return (
            <RunQueuesTableRow
              key={r.id}
              initiallyExpanded={
                index === 0 && r.runQueueItems.edges.length > 0
              }
              projectName={projectName}
              entityName={entityName}
              runQueue={r}
              launchAgentsMap={launchAgentsMap}
              canDeleteProjectAccessQueue={canDeleteProjectAccessQueue}
              isTeam={isTeam}
              refetchProject={refetchProject}
              setDeleteRunQueue={setDeleteRunQueue}
              projectWriteAccess={projectWriteAccess}
            />
          );
        })}
      {filteredRunQueues.length === 0 && (
        <S.Header>No run queues found matching {runQueuesFilter}.</S.Header>
      )}
    </>
  );
};

interface RunQueuesTableRowProps {
  runQueue: RunQueue;
  projectName: string;
  entityName: string;
  launchAgentsMap: {[key: number]: string};
  canDeleteProjectAccessQueue: boolean;
  initiallyExpanded: boolean;
  isTeam: boolean;
  projectWriteAccess: boolean;
  refetchProject: () => Promise<ApolloCurrentQueryResult<ProjectPageQueryData>>;
  setDeleteRunQueue(state: RunQueue): void;
}

const RunQueuesTableRow: React.FC<RunQueuesTableRowProps> = ({
  runQueue,
  projectName,
  entityName,
  launchAgentsMap,
  canDeleteProjectAccessQueue,
  initiallyExpanded,
  isTeam,
  projectWriteAccess,
  refetchProject,
  setDeleteRunQueue,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [addJob, setAddJob] = useState(false);
  const runQueueRunIds = runQueue.runQueueItems.edges.map(e => {
    return e.node.associatedRunId ?? null;
  });
  const filteredRunIds: string[] = runQueueRunIds.filter(isNotNullOrUndefined);
  const filter = runNamesFilter(filteredRunIds);
  const runStatesQuery = useRunsStateQuery({
    variables: {
      projectName,
      entityName,
      filters: JSON.stringify(toMongo(filter)),
    },
  });

  const runStates = useMemo(
    () =>
      runStatesQuery.data?.project?.runs?.edges.map(edge => {
        return {
          id: edge.node.name,
          state: edge.node.state,
          displayName: edge.node.displayName,
          urlName: edge.node.name,
        };
      }),
    [runStatesQuery]
  );
  const runningItems: RunQueueItem[] = useMemo(
    () =>
      runQueue.runQueueItems.edges.flatMap(e => {
        if (e.node.state === 'PENDING' || e.node.state === 'LEASED') {
          return [];
        }
        const runIdAndState = runStates?.find(
          rs => rs.id === e.node.associatedRunId
        );
        if (runIdAndState?.state === 'running') {
          return e.node;
        }
        return [];
      }),
    [runQueue.runQueueItems, runStates]
  );

  const runQueueItemsWithRunInfo: RunQueueItemWithRunInfo[] = useMemo(() => {
    return runQueue.runQueueItems.edges.flatMap(e => {
      if (e.node.state === 'PENDING' || e.node.state === 'LEASED') {
        return {
          ...e.node,
          runState: e.node.state === 'LEASED' ? 'starting' : 'queued',
        };
      } else if (e.node.state === 'CLAIMED') {
        const runIDAndState = runStates?.find(
          rs => rs.id === e.node.associatedRunId
        );
        if (
          runIDAndState == null ||
          runIDAndState.state === 'finished' ||
          runIDAndState.state === 'failed' ||
          runIDAndState.state === 'crashed'
        ) {
          return [];
        } else {
          return {
            ...e.node,
            runState: runIDAndState?.state ?? 'starting',
            runName: runIDAndState?.displayName,
            urlName: runIDAndState?.urlName,
          };
        }
      }
      return [];
    });
  }, [runQueue, runStates]);

  if (runStatesQuery.loading) {
    return <></>;
  }
  // TODO: add run queue item created by. Change disabled to be
  // for run queues that have runs not created by current viewer
  const projectAccessAndNotAdmin =
    runQueue.access === RunQueueAccessType.Project &&
    !canDeleteProjectAccessQueue;
  const options = [
    {
      key: 'create-job',
      text: 'Create Container Job',
      icon: <LegacyWBIcon name="rocket" />,
      disabled: !projectWriteAccess,
      onClick: () => {
        window.analytics?.track(
          'from run queue tab, open launch config modal',
          {
            entityName,
            projectName,
            queueName: runQueue.name,
          }
        );
        setAddJob(true);
      },
    },
    {
      key: 'delete',
      text: 'Delete queue',
      icon: <LegacyWBIcon name="delete" />,
      disabled: projectAccessAndNotAdmin,
      onClick: () => setDeleteRunQueue(runQueue),
    },
  ];
  return (
    <>
      {addJob && (
        <EditLaunchConfigModal
          entityName={entityName}
          projectName={projectName}
          runQueueName={runQueue.name}
          readOnly={!projectWriteAccess}
          onClose={() => setAddJob(false)}
        />
      )}
      <S.SuperTableRowOutter>
        <S.SuperTableRow onClick={() => setExpanded(!expanded)}>
          <S.SuperTableCellName>
            <S.QueueNameSpan expanded={expanded}>
              <LegacyWBIcon
                className={expanded ? 'open' : undefined}
                name="next"
              />
              {runQueue.name} ({runQueueItemsWithRunInfo.length})
            </S.QueueNameSpan>
          </S.SuperTableCellName>
          {runQueue.access === 'USER' && isTeam && (
            <S.SuperTableCell>
              <Popup
                content="Private - Only you can see this queue"
                inverted
                size="mini"
                position="top center"
                trigger={<LegacyWBIcon name="lock" />}
              />
            </S.SuperTableCell>
          )}
          <S.SuperTableCell>
            <Popup
              content={`${runningItems.length} active runs`}
              inverted
              size="mini"
              position="top center"
              trigger={
                <S.SuperTableSubCell>
                  {runningItems.length > 0 && <ActiveRunIndicator />}{' '}
                  <div style={{marginLeft: '3px'}}>{runningItems.length}</div>
                </S.SuperTableSubCell>
              }
            />
          </S.SuperTableCell>

          {projectWriteAccess && (
            <PopupDropdown
              position="bottom right"
              offset={'-45px, -1px'}
              trigger={
                <S.SuperTableCell
                  onClick={(e: React.SyntheticEvent) => {
                    e.stopPropagation();
                  }}>
                  <LegacyWBIcon name="overflow" size="big" />
                </S.SuperTableCell>
              }
              options={
                runQueue.name === 'default' ? options.slice(0, 1) : options
              }
            />
          )}
        </S.SuperTableRow>
        {expanded && <S.Linebreak />}
        {expanded && runQueue.runQueueItems.edges.length > 0 && (
          <S.SubTableDiv>
            <RunQueueItemsTable
              runQueue={runQueue}
              runQueueItemsWithRunInfo={runQueueItemsWithRunInfo}
              refetchProject={refetchProject}
              projectName={projectName}
              entityName={entityName}
              launchAgentsMap={launchAgentsMap}
            />
          </S.SubTableDiv>
        )}
      </S.SuperTableRowOutter>
    </>
  );
};

export interface RunQueueItemWithRunInfo extends RunQueueItem {
  runState: string;
  runName?: string | null;
  urlName?: string | null;
}

interface AddRunQueueModalProps {
  open: boolean;
  entityName: string;
  projectName: string;
  viewingEntity?: string;
  isTeam: boolean;
  runQueues: RunQueue[];
  refetchProject(): Promise<ApolloCurrentQueryResult<ProjectPageQueryData>>;
  onClose(): void;
}

const AddRunQueueModal: React.FC<AddRunQueueModalProps> = props => {
  const [createRunQueue] = useCreateRunQueueMutation();
  const [queueName, setQueueName] = useState<string | undefined>(undefined);
  const [isPrivate, setIsPrivate] = useState(false);

  const access =
    props.isTeam && !isPrivate
      ? RunQueueAccessType.Project
      : RunQueueAccessType.User;

  const {disabled, message} = useMemo(() => {
    if (queueName === DEFAULT_RUN_QUEUE_NAME && props.isTeam && isPrivate) {
      return {
        disabled: true,
        message: `"${DEFAULT_RUN_QUEUE_NAME}" runQueue name cannot be private`,
      };
    } else if (props.runQueues.some(rq => rq.name === queueName)) {
      return {
        disabled: true,
        message: 'a runQueue with that name already exists in this project',
      };
    } else if (queueName == null) {
      return {
        disabled: true,
        message: 'must select a queue name',
      };
    }
    return {disabled: false, message: null};
  }, [queueName, props.runQueues, isPrivate, props.isTeam]);

  const onCreateRunQueueClick = async () => {
    window.analytics?.track('create new run queue', {
      entityName: props.entityName,
      projectName: props.projectName,
      queueName,
      access,
    });
    if (queueName == null) {
      return;
    }
    const {data} = await createRunQueue({
      variables: {
        projectName: props.projectName,
        entityName: props.entityName,
        queueName,
        access,
      },
    });
    if (
      data == null ||
      !data.createRunQueue?.success ||
      data?.createRunQueue?.queueID == null
    ) {
      alert('Run Queue creation failed');
    } else {
      props.refetchProject();
    }
    props.onClose();
  };

  return (
    <S.Modal open={props.open} onClose={props.onClose}>
      <Modal.Content>
        <S.ModalTitle>Create Queue</S.ModalTitle>
        <S.ModalInputContainer>
          <S.Input
            size="mini"
            placeholder="ex. GPU Queue"
            onChange={(e: React.SyntheticEvent, {value}: InputOnChangeData) =>
              setQueueName(value)
            }
            value={queueName}
          />
        </S.ModalInputContainer>
        {props.isTeam && (
          <S.ModalText>
            <div>Private to user:</div>
            <S.Radio
              type="radio"
              toggle
              checked={isPrivate}
              onChange={() => setIsPrivate(!isPrivate)}
            />
          </S.ModalText>
        )}
        <S.ModalActions>
          <S.ButtonContainer>
            <Button onClick={props.onClose}>Cancel</Button>
          </S.ButtonContainer>
          <S.ButtonContainer>
            <Button
              tooltip={message}
              disabled={disabled}
              color="blue"
              onClick={() => onCreateRunQueueClick()}>
              Create Queue
            </Button>
          </S.ButtonContainer>
        </S.ModalActions>
      </Modal.Content>
    </S.Modal>
  );
};

interface DeleteRunQueueModalProps {
  open: boolean;
  runQueue: RunQueue;
  projectName: string;
  entityName: string;
  onClose(): void;
  onDelete(): void;
}

const DeleteRunQueueModal: React.FC<DeleteRunQueueModalProps> = ({
  open,
  runQueue,
  projectName,
  entityName,
  onClose,
  onDelete,
}) => {
  const [deleteRunQueue] = useDeleteRunQueuesMutation({
    variables: {queueIDs: [runQueue.id]},
  });
  const [allowDelete, setAllowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  return (
    <Modal className="runQueue-delete-modal" open={open} onClose={onClose}>
      <Modal.Content>
        <p>
          Are you sure you want to DELETE queue "{runQueue.name}"? All starting
          or queued runs will be removed everywhere. Finished runs from this
          queue will still be available. This action cannot be undone!
        </p>
        <Checkbox
          label={`Delete "${runQueue.name}"`}
          checked={allowDelete}
          onChange={() => {
            setAllowDelete(!allowDelete);
          }}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          color="red"
          disabled={!allowDelete || deleting}
          loading={deleting}
          onClick={async e => {
            window.analytics?.track('delete new run queue', {
              entityName,
              projectName,
              queueName: runQueue.name,
            });
            setDeleting(true);
            e.preventDefault();
            e.stopPropagation();
            await deleteRunQueue();
            setDeleting(false);
            onDelete();
          }}>
          Delete this queue
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
